import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineStyle } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import {
  SESSION_TF, SessionBandPrimitive, buildSessionBands,
  DayLinePrimitive, buildDayLines,
  baseChartOptions, TfButtons, SessionLegend, attachVerticalPan,
} from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const PIVOT_LINE_STYLE = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
};

const POLL_MS = {
  '1m': 30000, '5m': 60000, '15m': 120000,
  '30m': 180000, '1h': 300000, '4h': 600000, '1D': 900000,
};

export default function ChartWidget({ tf, setTf, symbol = 'SPY', setSymbol, tickers = ['SPY'] }) {
  const [showPivots, setShowPivots] = useState(true);
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const primitiveRef = useRef(null);
  const dayLineRef   = useRef(null);
  const pollRef        = useRef(null);
  const mountedRef     = useRef(true);
  const initialDone    = useRef(false);
  const resetVPanRef   = useRef(null);
  const pivotLinesRef  = useRef([]);

  // Create chart once on mount
  useEffect(() => {
    mountedRef.current = true;

    const chart = createChart(containerRef.current, baseChartOptions(containerRef.current.clientWidth || 300));
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    // Attach day-line primitive AFTER session bands so it renders on top of them.
    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    series.attachPrimitive(dayLine);

    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const { detach: detachVPan, reset } = attachVerticalPan(containerRef.current, [series]);
    resetVPanRef.current = reset;

    return () => {
      mountedRef.current = false;
      detachVPan();
      resetVPanRef.current = null;
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = dayLineRef.current = null;
    };
  }, []);

  // Refetch when timeframe or symbol changes
  useEffect(() => {
    initialDone.current = false;
    clearInterval(pollRef.current);

    resetVPanRef.current?.();
    seriesRef.current?.setData([]);
    primitiveRef.current?.clearBands();
    dayLineRef.current?.clearLines();

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/bars?symbol=${symbol}&tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!bars || !bars.length || !mountedRef.current) return;

        if (isInitial || !initialDone.current) {
          resetVPanRef.current?.();
          seriesRef.current.applyOptions({ autoscaleInfoProvider: (getDefault) => getDefault() });
          seriesRef.current.setData(bars);
          chartRef.current.priceScale('right').applyOptions({ autoScale: true });
          chartRef.current.timeScale().fitContent();
          initialDone.current = true;
        } else {
          seriesRef.current.update(bars[bars.length - 1]);
        }

        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(bars));
        } else {
          primitiveRef.current?.clearBands();
        }

        dayLineRef.current?.setLines(buildDayLines(bars));
      } catch { /* stale data shown until next poll */ }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 30000);
    return () => clearInterval(pollRef.current);
  }, [tf, symbol]);

  // Fetch and draw pivot levels when symbol or toggle changes
  useEffect(() => {
    if (!showPivots || !seriesRef.current) {
      // Remove existing lines
      for (const pl of pivotLinesRef.current) {
        try { seriesRef.current?.removePriceLine(pl); } catch {}
      }
      pivotLinesRef.current = [];
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;
        const res = await fetch(`${API_URL}/api/chart/pivot-levels?symbol=${symbol}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || !seriesRef.current) return;

        // Remove old lines
        for (const pl of pivotLinesRef.current) {
          try { seriesRef.current.removePriceLine(pl); } catch {}
        }
        pivotLinesRef.current = [];

        // Draw new lines
        for (const lv of (data.levels || [])) {
          const pl = seriesRef.current.createPriceLine({
            price: lv.price,
            color: lv.color,
            lineWidth: 1,
            lineStyle: PIVOT_LINE_STYLE[lv.style] ?? LineStyle.Solid,
            axisLabelVisible: true,
            title: lv.label,
          });
          pivotLinesRef.current.push(pl);
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
      for (const pl of pivotLinesRef.current) {
        try { seriesRef.current?.removePriceLine(pl); } catch {}
      }
      pivotLinesRef.current = [];
    };
  }, [symbol, showPivots]);

  const isSpx = symbol === 'SPX';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">

        {/* Symbol dropdown */}
        <div className="relative">
          <select
            value={symbol}
            onChange={e => setSymbol?.(e.target.value)}
            className="appearance-none text-xs font-bold rounded px-3 py-1.5 pr-7 border cursor-pointer focus:outline-none focus:border-blue-500"
            style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
          >
            {tickers.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
        </div>

        {/* SPX note — prices are SPY×10 proxy */}
        {isSpx && (
          <span className="text-xs text-gray-500">~SPY×10+35</span>
        )}

        <TfButtons tf={tf} setTf={setTf} />
        {SESSION_TF.has(tf) && <SessionLegend />}

        <div className="flex-1" />

        <button
          onClick={() => setShowPivots(p => !p)}
          className="text-xs font-medium px-2 py-1 rounded transition-colors"
          style={
            showPivots
              ? { backgroundColor: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f655' }
              : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
          }
        >Pivots</button>
      </div>
      <div ref={containerRef} />
    </div>
  );
}
